import { FC } from "react";
import { Alert, Typography } from "@mui/material";

import { Claim, ClaimStatus, FeeChange } from "../../../../../types/claims";
import { formatCurrency } from "../../../../helpers/dataHelper";
import StatusChip from "../../table-components/StatusChip";
import {
  BASELINE_FALLBACK_MESSAGES,
  formatFeeAmount,
  formatFeeMonth,
} from "../claimAppeals";
import {
  BeforeAfter,
  EffectPanel,
  NoneText,
  SummaryRow,
} from "./ClaimEditDialogParts";

const Status: FC<{ status: string | null | undefined }> = ({ status }) =>
  status ? <StatusChip status={status as ClaimStatus} /> : <>—</>;

const formatPaidAmount = (claim: Claim) =>
  claim.paidAmount != null ? formatCurrency(Number(claim.paidAmount)) : "—";

/**
 * What recording, removing or joining a manual payment does to the claim: paid
 * amount, status and success fee, before and after (REV-2584). `fee` is null
 * when the effect couldn't be computed (a failed preview, or billing failing on
 * the claim); the claim's current values are shown instead, except for a
 * `paidAmountAfter` the caller already knows (a join just stops counting the
 * manual amount).
 */
const PaymentEffect: FC<{
  claim: Claim;
  fee: FeeChange | null;
  paidAmountAfter?: string | null;
}> = ({ claim, fee, paidAmountAfter }) => (
  <EffectPanel title="Effect on the claim">
    <SummaryRow label="Paid amount">
      {fee ? (
        <BeforeAfter
          before={formatFeeAmount(fee.paidAmount.before)}
          after={formatFeeAmount(fee.paidAmount.after)}
        />
      ) : paidAmountAfter != null ? (
        <BeforeAfter
          before={formatPaidAmount(claim)}
          after={formatFeeAmount(paidAmountAfter)}
        />
      ) : claim.paidAmount != null ? (
        formatCurrency(claim.paidAmount)
      ) : (
        "—"
      )}
    </SummaryRow>

    <SummaryRow label="Status">
      {fee ? (
        <BeforeAfter
          same={fee.claimStatus.before === fee.claimStatus.after}
          before={<Status status={fee.claimStatus.before} />}
          after={<Status status={fee.claimStatus.after} />}
        />
      ) : (
        <Status status={claim.status} />
      )}
    </SummaryRow>

    <SummaryRow label="Success fee">
      {fee ? (
        <BeforeAfter
          before={formatFeeAmount(fee.successFee.before)}
          after={formatFeeAmount(fee.successFee.after)}
        />
      ) : (
        <NoneText>Not available yet</NoneText>
      )}
    </SummaryRow>

    {fee && fee.restatedMonths.length > 0 && (
      <Typography
        sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.5 }}
      >
        Restates the success fee already booked for{" "}
        {fee.restatedMonths.map(formatFeeMonth).join(", ")}.
      </Typography>
    )}

    {fee?.baselineFallback && (
      <Alert severity="warning" sx={{ maxWidth: "100%", mt: 1 }}>
        {BASELINE_FALLBACK_MESSAGES[fee.baselineFallback]}
      </Alert>
    )}

    {!fee && (
      <Typography
        sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.5 }}
      >
        {paidAmountAfter != null
          ? "Status and success fee are the claim's current values: their change couldn't be computed."
          : "These are the claim's current values: the effect of this change couldn't be computed."}{" "}
        The claim refreshes once it's saved.
      </Typography>
    )}
  </EffectPanel>
);

export default PaymentEffect;
